import React from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';

import NvIcon from 'shared/components/nv-icon';
import NvPopover from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import NvTooltip from '../nv-tooltip';

export interface FileProps {
  id: string;
  uniqueId: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  url: string;
  error?: string;
  resourceId?: number;
}

const buttonsContainerStyle = css`
  display: flex;
  align-items: center;
  gap: ${standardSpacing}px;
`;

interface NvInlineUploadedFileProps {
  resource: FileProps;
  onDelete: (id: string) => void;
  deletingResourceId: string | null;
  setDeletingResourceId: (id: string | null) => void;
  deletePopoverText: string;
  isProcessing?: boolean;
}

const NvInlineUploadedFile: React.FC<NvInlineUploadedFileProps> = ({
  resource,
  onDelete,
  deletingResourceId,
  setDeletingResourceId,
  deletePopoverText,
  isProcessing,
}) => {
  const downloadFile = () => {
    if (resource?.url) {
      window.open(resource.url, '_blank');
    }
  };

  const hasError = resource.error;
  const iconClass = hasError ? ' text-danger' : '';

  return (
    <div key={resource.id} className='file-item d-flex align-items-center justify-content-between mb-2'>
      <div className='file-info d-flex align-items-center flex-grow-1'>
        <NvIcon icon='read' size='smallest' className={`mr-2${iconClass}`} />
        <div className={`file-name font-weight-bold mr-4${hasError ? ' text-danger' : ''}`} data-qa={`file-${resource.id}`}>
          {resource.fileName}{isProcessing && <span className='text-danger'>{' '}{t.FILE_UPLOAD.FILE_PROCESSING()}</span>}
        </div>
      </div>
      <div css={buttonsContainerStyle}>
        <NvTooltip placement='top' text={t.FILE_UPLOAD.VIEW_FILE()}>
          <NvIcon
            icon='download'
            size='xs-small'
            className='text-primary cursor-pointer'
            onClick={downloadFile}
            data-qa={`download-resource-${resource.id}`}
          />
        </NvTooltip>
        {!hasError && (
          <NvPopover
            show={deletingResourceId === resource.id}
            rootClose
            onHide={() => setDeletingResourceId(null)}
            placement='top-end'
            content={(
              <NvConfirmationPopover
                header={t.FORM.ARE_YOU_SURE()}
                message={deletePopoverText}
                onCancel={() => setDeletingResourceId(null)}
                onConfirm={() => {
                  onDelete(resource.id);
                  setDeletingResourceId(null);
                }}
                cancelBtnText={t.FORM.CANCEL()}
                confirmBtnText={t.FORM.DELETE()}
              />
            )}
          >
            <NvTooltip placement='top' text={t.FILE_UPLOAD.DELETE_FILE()}>
              <NvIcon
                icon='trash'
                size='xs-small'
                className='text-primary cursor-pointer'
                onClick={() => setDeletingResourceId(resource.id)}
                data-qa={`delete-resource-${resource.id}`}
              />
            </NvTooltip>
          </NvPopover>
        )}
        {hasError && (
          <NvTooltip placement='top' text={t.FILE_UPLOAD.DELETE_FILE()}>
            <NvIcon
              icon='close'
              size='xs-small'
              className={`${iconClass} cursor-pointer`}
              onClick={() => onDelete(resource.id)}
              data-qa={`remove-error-resource-${resource.id}`}
            />
          </NvTooltip>
        )}
      </div>
    </div>
  );
};

export default NvInlineUploadedFile;
